import React from "react";
import { Drawer, Descriptions, Tag, Divider, Table } from "antd";
import dayjs from "dayjs";
import { StockTransfer } from "../../../Types";
import { DRAWER_DETAIL_WIDTH, drawerTitle } from "../../../components";

interface Props {
  transfer: StockTransfer | null;
  onClose: () => void;
}

export const TransferDetailDrawer: React.FC<Props> = ({
  transfer,
  onClose,
}) => {
  const columns = [
    {
      title: "Product",
      key: "product",
      render: (_: any, r: any) => (
        <div>
          <div className="text-xs font-medium text-gray-800">
            {r.from_product_title}
          </div>
          <div className="text-[11px] text-gray-400">{r.batch_no}</div>
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      align: "right" as const,
      render: (val: number) => (
        <span className="font-semibold text-xs">{val}</span>
      ),
    },
    {
      title: "Cost Price",
      dataIndex: "cost_price",
      align: "right" as const,
      render: (val: number) => (
        <span className="text-xs">Rs. {Number(val).toFixed(2)}</span>
      ),
    },
    {
      title: "Retail Price",
      dataIndex: "retail_price",
      align: "right" as const,
      render: (val: number) => (
        <span className="text-xs text-green-600">
          Rs. {Number(val).toFixed(2)}
        </span>
      ),
    },
  ];

  return (
    <Drawer
      title={drawerTitle("Transfer Details")}
      open={!!transfer}
      onClose={onClose}
      width={DRAWER_DETAIL_WIDTH}
    >
      {transfer && (
        <div className="flex flex-col gap-4">
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Transfer No">
              <span className="font-medium">{transfer.transfer_no}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {dayjs(transfer.created_at).format("DD MMM YYYY, hh:mm A")}
            </Descriptions.Item>
            <Descriptions.Item label="Source Store">
              <div className="flex items-center gap-1.5">
                <Tag color="blue" className="m-0">{transfer.from_store_name}</Tag>
                {(transfer as any).from_store_type && (
                  <Tag
                    color={(transfer as any).from_store_type === "vehicle" ? "orange" : "cyan"}
                    className="font-semibold text-[10px] rounded-md"
                  >
                    {(transfer as any).from_store_type === "vehicle" ? "🚚 Vehicle" : "🏢 Shop"}
                  </Tag>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Destination Store">
              <div className="flex items-center gap-1.5">
                <Tag color="purple" className="m-0">{transfer.to_store_name}</Tag>
                {(transfer as any).to_store_type && (
                  <Tag
                    color={(transfer as any).to_store_type === "vehicle" ? "orange" : "cyan"}
                    className="font-semibold text-[10px] rounded-md"
                  >
                    {(transfer as any).to_store_type === "vehicle" ? "🚚 Vehicle" : "🏢 Shop"}
                  </Tag>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Operator">
              {transfer.creator_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color="green">{transfer.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Note">
              {transfer.note || "-"}
            </Descriptions.Item>
          </Descriptions>

          <Divider className="my-1" />

          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Transferred Items ({transfer.items?.length || 0})
          </div>

          <Table
            dataSource={transfer.items || []}
            rowKey="id"
            size="small"
            pagination={false}
            columns={columns}
          />
        </div>
      )}
    </Drawer>
  );
};
